/**
 * Dashboard Cache Utility
 * Provides client-side caching for dashboard API responses
 */

const CACHE_PREFIX = 'dashboard_cache_v2_';
const DEFAULT_TTL = 20 * 60 * 1000; // 20 minutes in milliseconds

/**
 * Generate cache key for a specific endpoint
 * @param {string} variant - Dashboard variant ('global', 'hybrid', 'tech')
 * @param {string} risk - Risk profile ('Averse', 'Neutral', 'Seeker', or 'all')
 * @param {string} endpoint - Endpoint name ('allocation', 'performance', etc.)
 * @returns {string} Cache key
 */
function generateCacheKey(variant, risk, endpoint) {
    return `${CACHE_PREFIX}${variant}_${risk}_${endpoint}`;
}

/**
 * Get cached data if it exists and is not stale
 * @param {string} variant - Dashboard variant
 * @param {string} risk - Risk profile
 * @param {string} endpoint - Endpoint name
 * @param {number} ttl - Time to live in milliseconds (default: 20 minutes)
 * @returns {Object|null} Cached data or null if not found/stale
 */
export function get(variant, risk, endpoint, ttl = DEFAULT_TTL) {
    try {
        const cacheKey = generateCacheKey(variant, risk, endpoint);
        const cachedStr = localStorage.getItem(cacheKey);
        
        if (!cachedStr) {
            return null;
        }
        
        const cached = JSON.parse(cachedStr);
        const now = Date.now();
        const age = now - cached.timestamp;
        
        // Check if cache is stale
        if (age > ttl) {
            // Remove stale cache
            localStorage.removeItem(cacheKey);
            return null;
        }
        
        return cached.data;
    } catch (error) {
        console.error('Error reading from cache:', error);
        return null;
    }
}

/**
 * Store data in cache
 * @param {string} variant - Dashboard variant
 * @param {string} risk - Risk profile
 * @param {string} endpoint - Endpoint name
 * @param {*} data - Data to cache
 */
export function set(variant, risk, endpoint, data) {
    try {
        const cacheKey = generateCacheKey(variant, risk, endpoint);
        const cacheEntry = {
            data,
            timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
        console.error('Error writing to cache:', error);
        // If storage is full, try to clear old entries
        if (error.name === 'QuotaExceededError') {
            clear();
            // Retry once
            try {
                const cacheKey = generateCacheKey(variant, risk, endpoint);
                const cacheEntry = {
                    data,
                    timestamp: Date.now()
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
            } catch (retryError) {
                console.error('Error retrying cache write:', retryError);
            }
        }
    }
}

/**
 * Check if cached data is stale
 * @param {string} variant - Dashboard variant
 * @param {string} risk - Risk profile
 * @param {string} endpoint - Endpoint name
 * @param {number} ttl - Time to live in milliseconds
 * @returns {boolean} True if stale or missing, false if fresh
 */
export function isStale(variant, risk, endpoint, ttl = DEFAULT_TTL) {
    try {
        const cacheKey = generateCacheKey(variant, risk, endpoint);
        const cachedStr = localStorage.getItem(cacheKey);
        
        if (!cachedStr) {
            return true;
        }
        
        const cached = JSON.parse(cachedStr);
        const now = Date.now();
        const age = now - cached.timestamp;
        
        return age > ttl;
    } catch (error) {
        return true;
    }
}

/**
 * Clear all dashboard cache entries
 */
export function clear() {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.error('Error clearing cache:', error);
    }
}

/**
 * Clear cache for a specific variant and risk profile
 * @param {string} variant - Dashboard variant
 * @param {string} risk - Risk profile (optional, clears all risks if not provided)
 */
export function clearForVariant(variant, risk = null) {
    try {
        const keys = Object.keys(localStorage);
        const prefix = risk 
            ? `${CACHE_PREFIX}${variant}_${risk}_`
            : `${CACHE_PREFIX}${variant}_`;
        
        keys.forEach(key => {
            if (key.startsWith(prefix)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.error('Error clearing cache for variant:', error);
    }
}

/**
 * Get cache statistics (for debugging)
 * @returns {Object} Cache statistics
 */
export function getStats() {
    try {
        const keys = Object.keys(localStorage);
        const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
        const stats = {
            totalEntries: cacheKeys.length,
            entries: {}
        };
        
        cacheKeys.forEach(key => {
            try {
                const cachedStr = localStorage.getItem(key);
                if (cachedStr) {
                    const cached = JSON.parse(cachedStr);
                    const age = Date.now() - cached.timestamp;
                    stats.entries[key] = {
                        age: Math.floor(age / 1000), // Age in seconds
                        ageFormatted: formatAge(age)
                    };
                }
            } catch (e) {
                // Skip invalid entries
            }
        });
        
        return stats;
    } catch (error) {
        console.error('Error getting cache stats:', error);
        return { totalEntries: 0, entries: {} };
    }
}

/**
 * Format age in human-readable format
 * @param {number} ageMs - Age in milliseconds
 * @returns {string} Formatted age string
 */
function formatAge(ageMs) {
    const seconds = Math.floor(ageMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    } else {
        return `${seconds}s`;
    }
}

































